<script setup lang="ts">
const { $localePath, $containment } = useNuxtApp();
const { t } = useTexts();
const { parameters, gaClientId } = useParameters();
const { closeModal, openToaster } = useLayoutStore();
const { bonusWallet, realWallet } = storeToRefs(useWalletStore());
const { currentWithdrawMethod } = storeToRefs(useWalletStore());
const { selectWithdrawMethod } = useWalletStore();
const { updateWallet, showMoneyFormat, walletHasFirstDeposit } = useWallet();
const { isVerifiedToWithdraw } = useUserValidations();
const { getMinValueToTriggerModule } = useModulesValidation();
const { onContactValidationComplete } = useAuthStore();

const {
  userCurrency,
  userCountry,
  userLanguage,
  userBankAccounts,
  fetchUserProfile,
} = useUserData();

const { numberOfWithdrawMethods, defaultSelectedWithdrawMethod } =
  usePayments();

const { isLoading } = storeToRefs(usePaymentsStore());

const hasAnyWithdrawMethod = computed(() => {
  return numberOfWithdrawMethods.value >= 1;
});

const minWithdrawAmountBrand = computed(
  () => parameters.value?.min_withdraw_value || 100,
);

const minWithdrawAmountUser = computed(
  () => realWallet.value.minWithdrawAmount,
);

const maxWithdrawAmountBrand = computed(
  () => parameters.value?.max_withdraw_value || 50000,
);

const maxWithdrawAmountUser = computed(
  () => realWallet.value.maxWithdrawAmount,
);

const minWithdrawAmount = computed(() => {
  return minWithdrawAmountUser.value
    ? minWithdrawAmountUser.value
    : minWithdrawAmountBrand.value || 100;
});

const maxWithdrawAmount = computed(() => {
  return maxWithdrawAmountUser.value
    ? maxWithdrawAmountUser.value
    : maxWithdrawAmountBrand.value;
});

const userCurrentCreditAmount = computed(() => realWallet.value.credit);

const userWalletHasBonus = computed(() => {
  return bonusWallet.value.credit > 0;
});

const isConfirmedBonusLoss = ref<boolean>(false);

const handdleConfirmBonusLoss = () => {
  isConfirmedBonusLoss.value = true;
};

const showBonusLossAlert = computed(() => {
  return userWalletHasBonus.value && !isConfirmedBonusLoss.value;
});

const showWithdrawConfirmAlert = ref<boolean>(false);

const closeWithdrawConfirmAlert = () => {
  showWithdrawConfirmAlert.value = !showWithdrawConfirmAlert.value;
};

const withdrawMethodIsPix = computed(() => {
  return (
    currentWithdrawMethod.value === 'pix' ||
    currentWithdrawMethod.value === 'pixtopay' ||
    currentWithdrawMethod.value === 'oktopay' ||
    currentWithdrawMethod.value === 'paybrokers' ||
    currentWithdrawMethod.value === 'anspacepay' ||
    currentWithdrawMethod.value === 'pay2free' ||
    currentWithdrawMethod.value === 'paag' ||
    currentWithdrawMethod.value === 'triopay' ||
    currentWithdrawMethod.value === 'riopag' ||
    currentWithdrawMethod.value === 'starkbank' ||
    currentWithdrawMethod.value === 'efibank' ||
    currentWithdrawMethod.value === 'genialbank' ||
    currentWithdrawMethod.value === 'iugu'
  );
});

const withdrawMethodIsSpei = computed(() => {
  return (
    currentWithdrawMethod.value === 'spei' ||
    currentWithdrawMethod.value === 'oxxo'
  );
});

const withdrawMethodIsWithphone = computed(() => {
  return currentWithdrawMethod.value === 'astropay';
});

const withdrawRequestAmount = ref<number>(0);
const isLoadingWithdraw = ref<boolean>(false);
const showWithdrawRules = ref<boolean>(false);
const isWithdrawSuccess = ref<boolean>(false);

const showLoading = computed(() => {
  return isLoadingWithdraw.value || isLoading.value;
});

const toggleWithdrawRules = () => {
  showWithdrawRules.value = !showWithdrawRules.value;
};

const withdrawPixKeyType = ref<string>();
const withdrawPixKeyValue = ref<string>();
const withdrawPixBankAccountId = ref<number>();
const withdrawPixKeyError = ref<string>();

const withdrawSpeiBankType = ref<string>();
const withdrawSpeiBankCode = ref<string>();
const withdrawSpeiBankNumber = ref<string>();

const withdrawPhoneNumber = ref<string>();
const withdrawPhoneDdi = ref<string>();

const confirmRequestWithdraw = ({
  withdrawAmount,
  selectedKeyType,
  selectedKeyValue,
  pixKeyError,
  selectedBankType,
  selectedBankCode,
  selectedBankNumber,
  selectedPhoneNumber,
  selectedPhoneDdi,
  selectedBankAccountId,
}: {
  withdrawAmount: number;
  selectedKeyType?: string;
  selectedKeyValue?: string;
  pixKeyError?: string;
  selectedBankType?: string;
  selectedBankCode?: string;
  selectedBankNumber?: string;
  selectedPhoneNumber?: string;
  selectedPhoneDdi?: string;
  selectedBankAccountId?: number;
}) => {
  withdrawRequestAmount.value = withdrawAmount;

  if (withdrawMethodIsPix.value) {
    withdrawPixKeyType.value = selectedKeyType;
    withdrawPixKeyValue.value = selectedKeyValue;
    withdrawPixBankAccountId.value = selectedBankAccountId;
    withdrawPixKeyError.value = pixKeyError;

    if (withdrawPixKeyType.value === 'PHONE') {
      withdrawPhoneNumber.value = selectedPhoneNumber;
      withdrawPhoneDdi.value = selectedPhoneDdi;
    }

    if (withdrawPixKeyError.value) {
      openToaster({
        message: withdrawPixKeyError.value,
        type: 'error',
      });

      return;
    }

    const { isBankAccountEnabled } = useFeatureFlagsJson();

    if (userBankAccounts.value.length >= 1 && isBankAccountEnabled.value) {
      if (!withdrawPixBankAccountId.value) {
        openToaster({
          message: t('account.wallet.withdraw_modal.bank_account_error'),
          type: 'error',
        });

        return;
      }
    } else if (!withdrawPixKeyType.value || !withdrawPixKeyValue.value) {
      openToaster({
        message: t('account.wallet.withdraw_modal.pix_key_error'),
        type: 'error',
      });

      return;
    }
  }

  if (withdrawMethodIsSpei.value) {
    withdrawSpeiBankType.value = selectedBankType;
    withdrawSpeiBankCode.value = selectedBankCode;
    withdrawSpeiBankNumber.value = selectedBankNumber;

    if (!withdrawSpeiBankType.value || !withdrawSpeiBankNumber.value) {
      openToaster({
        message: t('account.wallet.withdraw_modal.spei_account_error'),
        type: 'error',
      });

      return;
    }
  }

  if (withdrawMethodIsWithphone.value) {
    withdrawPhoneNumber.value = selectedPhoneNumber;
    withdrawPhoneDdi.value = selectedPhoneDdi;

    if (!withdrawPhoneNumber.value || !withdrawPhoneDdi.value) {
      openToaster({
        message: t('account.wallet.withdraw_modal.phone_error'),
        type: 'error',
      });

      return;
    }
  }

  showWithdrawConfirmAlert.value = true;
};

const onCompleteRegisterClick = async () => {
  const { isValidationModuleEnabled } = useModulesValidation();

  if (isValidationModuleEnabled('first_withdraw')) {
    const { fetchUserProfile } = useUserData();
    const { $containment } = useNuxtApp();

    isLoadingWithdraw.value = true;

    await fetchUserProfile({ force: true });

    await $containment.fetchCurrentPending('first_withdraw');

    if ($containment.hasPendencyOn('first_withdraw')) {
      isLoadingWithdraw.value = false;

      await $containment.handle('first_withdraw', () => {
        openToaster({
          message: t('account.wallet.withdraw_modal.validation_success'),
          type: 'success',
        });
      });
    }
  } else {
    await navigateTo({
      path: $localePath('/user/account'),
    });
  }
};

async function onRequestWithdraw() {
  if (!withdrawRequestAmount.value || !currentWithdrawMethod.value) {
    return;
  }

  const isDevWithdraw = isDev() && false;

  onContactValidationComplete(() => {
    showWithdrawConfirmAlert.value = false;
    requestWithdraw();
  });

  if (isDevWithdraw) {
    requestWithdraw();
  } else if (withdrawMethodIsPix.value) {
    if (withdrawRequestAmount.value >= getMinValueToTriggerModule('withdraw')) {
      await $containment.handle('withdraw', () => {
        requestWithdraw();
      });
    } else {
      requestWithdraw();
    }
  } else {
    requestWithdraw();
  }
}

async function requestWithdraw() {
  isLoadingWithdraw.value = true;

  const params: Record<string, unknown> = {
    amount: withdrawRequestAmount.value,
    method: currentWithdrawMethod.value,
  };

  if (withdrawPixBankAccountId.value) {
    params.bank_id = withdrawPixBankAccountId.value;
  } else if (withdrawPixKeyType.value && withdrawPixKeyValue.value) {
    params.pix_key_type = withdrawPixKeyType.value;
    params.pix_key_value = withdrawPixKeyValue.value;
    if (
      withdrawPixKeyType.value === 'PHONE' &&
      withdrawPhoneNumber.value &&
      withdrawPhoneDdi.value
    ) {
      params.phone_number = withdrawPhoneNumber.value;
      params.phone_ddi = withdrawPhoneDdi.value;
    }
  }

  if (withdrawPhoneNumber.value && withdrawPhoneDdi.value) {
    params.phone_number = withdrawPhoneNumber.value;
    params.phone_ddi = withdrawPhoneDdi.value;
  }

  if (withdrawSpeiBankType.value && withdrawSpeiBankNumber.value) {
    params.spei_bank_type = withdrawSpeiBankType.value;
    params.spei_bank_number = withdrawSpeiBankNumber.value;
    if (withdrawSpeiBankCode.value) {
      params.spei_bank_code = withdrawSpeiBankCode.value;
    }
  }

  if (gaClientId.value) {
    params.ga_client_id = gaClientId.value;
  }

  const { $kyc } = useNuxtApp();
  const { temporaryPassword } = useModulesValidation();

  if ($kyc?.current?.id) {
    params.kyc_id = $kyc.current.id;
  }

  if (temporaryPassword.get()) {
    params.password_check = temporaryPassword.get();
  }

  const { ApiService } = useApiService();

  await ApiService.post('/new-withdraws', params)
    .then((res) => {
      if (res.data.success) {
        const message = res.data.message
          ? res.data.message
          : t('account.wallet.withdraw_modal.withdraw_success');

        openToaster({
          message,
          type: 'success',
        });

        isWithdrawSuccess.value = true;

        setTimeout(async () => {
          await updateWallet();
          await fetchUserProfile({ force: true });
        }, 1500);

        return;
      }

      openToaster({
        message: res.data.message,
        type: 'error',
      });
    })
    .catch((error) => {
      const message =
        error.data?.message ||
        error.message ||
        error.exception ||
        error.statusText ||
        error ||
        t('account.wallet.withdraw_modal.server_error');

      openToaster({
        message,
        type: 'error',
      });
    })
    .finally(() => {
      temporaryPassword.clear();
      isLoadingWithdraw.value = false;
    });
}

const goToChangeCountry = () => {
  closeModal();
  navigateTo({
    path: $localePath('/user/account'),
    query: {
      highlight: 'infos',
    },
  });
};

onMounted(() => {
  selectWithdrawMethod(defaultSelectedWithdrawMethod.value);
});
</script>

<template>
  <ClientOnly>
    <div class="w-full flex flex-col gap-5 p-6">
      <BaseLoading
        class="!bg-auth-bg-primary z-50"
        :icon-loader="true"
        :show="showLoading"
      />

      <WithdrawCommomTitle
        v-if="!isWithdrawSuccess"
        :show-withdraw-rules="showWithdrawRules"
        @toggle-withdraw-rules="toggleWithdrawRules"
      />

      <template v-if="showWithdrawRules">
        <WithdrawRules />
      </template>

      <template v-else-if="isWithdrawSuccess">
        <WithdrawInfoCashoutSuccess :amount="withdrawRequestAmount" />
      </template>

      <template v-else-if="showWithdrawConfirmAlert">
        <template
          v-if="
            withdrawRequestAmount >=
              getMinValueToTriggerModule('first_withdraw') &&
            !isVerifiedToWithdraw
          "
        >
          <div :class="$style.alertConfirmMessage">
            <img
              alt="Email icon"
              aria-hidden="true"
              :class="$style.alertIcon"
              :src="getAssetsUrlForCDN('/assets/images/svg/complete.svg')"
            />

            <p
              v-html="
                $t('account.wallet.withdraw_modal.full_validation_alert', {
                  amount: showMoneyFormat({
                    value: getMinValueToTriggerModule('first_withdraw') / 100,
                  }),
                })
              "
            />

            <UserAccountWithdrawAlert
              :on-top="true"
              :show-text="false"
              :without-bg="true"
            />

            <div :class="[$style.actions]">
              <button
                :class="[$style.complete]"
                @click="onCompleteRegisterClick"
              >
                {{ $t('account.wallet.withdraw_modal.complete') }}
              </button>
              <button
                :class="[$style.false]"
                @click="closeWithdrawConfirmAlert"
              >
                {{ $t('account.wallet.withdraw_modal.cancel') }}
              </button>
            </div>
          </div>
        </template>

        <div
          v-else
          :class="$style.alertConfirmMessage"
        >
          <img
            alt="Email icon"
            aria-hidden="true"
            :class="$style.alertIcon"
            :src="getAssetsUrlForCDN('/assets/images/svg/withdraw.svg')"
          />

          <h3 v-html="$t('account.wallet.withdraw_modal.withdraw_alert')"></h3>

          <ul :class="$style.withdrawDetails">
            <li>
              {{ $t('account.wallet.withdraw_modal.detail_value') }}:
              <strong>
                {{ showMoneyFormat({ value: withdrawRequestAmount / 100 }) }}
              </strong>
            </li>
            <li v-if="withdrawMethodIsPix && withdrawPixKeyValue">
              {{ $t('account.wallet.withdraw_modal.detail_pix_key') }}:
              <strong>{{ withdrawPixKeyValue }}</strong>
            </li>
            <li v-else-if="withdrawMethodIsSpei && withdrawSpeiBankNumber">
              {{ $t('account.user_data.mex.account_number') }}:
              <strong>{{ withdrawSpeiBankNumber }}</strong>
            </li>
            <li v-else-if="withdrawMethodIsWithphone">
              {{ $t('account.phone') }}:
              <strong v-if="withdrawPhoneNumber && withdrawPhoneDdi">
                {{ withdrawPhoneNumber + withdrawPhoneDdi }}
              </strong>
              <strong v-else-if="withdrawPhoneNumber">
                {{ withdrawPhoneNumber }}
              </strong>
              <strong v-else>
                {{ $t('general.error') }}
              </strong>
            </li>
          </ul>

          <div
            v-if="userWalletHasBonus"
            class="alert alert-warning -mt-2"
            v-html="$t('account.wallet.withdraw_modal.bonus_second_alert')"
          >
          </div>

          <div :class="[$style.actions]">
            <button
              :class="[$style.true]"
              @click="onRequestWithdraw()"
            >
              {{ $t('account.wallet.withdraw_modal.confirm') }}
            </button>
            <button
              :class="[$style.false]"
              @click="closeWithdrawConfirmAlert"
            >
              {{ $t('account.wallet.withdraw_modal.cancel') }}
            </button>
          </div>
        </div>
      </template>

      <template v-else-if="showBonusLossAlert">
        <div :class="[$style.alertConfirmMessage]">
          <p v-html="$t('account.wallet.withdraw_modal.bonus_alert')"></p>
          <div :class="[$style.actions]">
            <button
              :class="[$style.true]"
              @click="handdleConfirmBonusLoss"
            >
              {{ $t('account.wallet.withdraw_modal.confirm') }}
            </button>
            <button
              :class="[$style.false]"
              @click="closeModal()"
            >
              {{ $t('account.wallet.withdraw_modal.cancel') }}
            </button>
          </div>
        </div>
      </template>

      <template v-else-if="!hasAnyWithdrawMethod">
        <div class="alert alert-warning">
          {{
            $t('deposit.general.no_pay_method', {
              currency: userCurrency,
              country: userCountry,
            })
          }}
        </div>

        <div
          v-if="!walletHasFirstDeposit"
          class="alert alert-success"
        >
          <button @click="goToChangeCountry">
            {{ $t('deposit.general.can_change_country') }}
          </button>
        </div>
      </template>

      <template v-else>
        <WithdrawMethodSelector
          v-if="hasAnyWithdrawMethod && !isLoading"
          :selected-withdraw-method="currentWithdrawMethod"
          @select-withdraw-method="selectWithdrawMethod"
        />

        <WithdrawCommomSection
          :initial-amount="withdrawRequestAmount"
          :max-withdraw-amount="maxWithdrawAmount"
          :min-withdraw-amount="minWithdrawAmount"
          :user-country="userCountry"
          :user-currency="userCurrency"
          :user-current-credit-amount="userCurrentCreditAmount"
          :user-language="userLanguage"
          :user-wallet-has-bonus="userWalletHasBonus"
          :withdraw-method="currentWithdrawMethod"
          :withdraw-method-is-pix="withdrawMethodIsPix"
          :withdraw-method-is-spei="withdrawMethodIsSpei"
          :withdraw-method-is-withphone="withdrawMethodIsWithphone"
          @confirm-request-withdraw="confirmRequestWithdraw"
        />
      </template>
    </div>
  </ClientOnly>
</template>

<style module lang="postcss">
.alertConfirmMessage {
  @apply -mt-1 z-10 relative w-full mx-auto rounded bg-bg-secondary py-5 px-6 flex flex-col justify-center items-center gap-4 text-center text-auth-texts text-lg;

  p {
    @apply text-lg;
  }

  h3 {
    @apply text-base md:text-xl;
  }

  .alertIcon {
    @apply w-28 h-28 mx-auto mt-2;
  }

  .withdrawDetails {
    @apply flex flex-col gap-1 font-semibold mb-4 text-sm md:text-base;

    strong {
      @apply font-bold text-primary;
    }
  }

  .actions {
    @apply flex justify-between gap-3 w-full;

    button {
      @apply px-4 py-2 rounded-md text-white flex-1 capitalize text-xs xs:text-sm md:text-base font-semibold;

      &.true {
        @apply bg-success;
      }

      &.complete {
        @apply bg-auth-button-bg text-auth-button-text px-4 md:px-7 whitespace-nowrap;
      }

      &.false {
        @apply bg-error;
      }
    }
  }
}

.kycValidation {
  @apply p-0;
  .continueButton {
    @apply px-4 py-2 rounded text-white flex-1 text-xs font-semibold bg-success flex items-center justify-center gap-1 m-0;
  }
}
</style>
